import React, { useMemo, useState } from "react";

type Tier = "bronze" | "silver" | "gold" | "platinum" | "diamond";
type VoteChoice = "yes" | "no" | null;

interface MergeProposalProps {
  account?: string | null;                 // wallet addr or null if not connected
  userTier?: string | null;                  // user's staking tier (null if not staked)
  eligible?: boolean;                      // true if user is staking APAD and can vote
  votingOpen?: boolean;                    // gate if voting is live
  votingStartsAt?: string;                 // ISO string for display if not yet open
  currentUserVote?: string | null;            // previously cast vote (if any)
  onCastVote?: (choice: string) => Promise<void> | void;
}

export default function MergeProposalVoteCard({
  account,
  userTier = null,
  eligible = false,
  votingOpen = true,
  votingStartsAt,
  currentUserVote = null,
  onCastVote
}: MergeProposalProps) {
  const [pending, setPending] = useState(false);
  const [localChoice, setLocalChoice] = useState<string | null>(currentUserVote);

  const tiers: Record<Tier, number> = useMemo(() => ({
    bronze: 1,
    silver: 2,
    gold: 3,
    platinum: 4,
    diamond: 5
  }), []);

  const tierLabel = (t?: string | null) =>
    t ? `${capitalize(t)} (${tiers[t]} vote${tiers[t] > 1 ? "s" : ""})` : "Not staked";

  const canVote = votingOpen && eligible && !!account;

  async function handleSubmit() {
    if (!canVote || !localChoice) return;
    try {
      setPending(true);
      await onCastVote?.(localChoice + "");
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="p-6 w-full mx-auto">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <h2 className="font-bold text-white text-2xl">Proposal: Merge AlphPad → AlphBanx</h2>
        <div className="text-right">
          <p className="text-sm text-gray-400">Status:</p>
          {votingOpen ? (
            <span className="inline-block px-2 py-1 rounded bg-green-900/40 text-green-300 text-xs font-semibold">
              Voting Open
            </span>
          ) : (
            <span className="inline-block px-2 py-1 rounded bg-yellow-900/40 text-yellow-300 text-xs font-semibold">
              Voting Ended
            </span>
          )}
        </div>
      </div>

      {/* Proposal Summary */}
      <div className="mb-6 grid gap-4 md:grid-cols-2">
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <h3 className="text-white font-semibold mb-2">What’s changing & why</h3>
          <ul className="text-gray-300 text-sm list-disc ml-5 space-y-1">
            <li>Combine AlphPad + AlphBanx dApps under the <span className="font-semibold">BanX SuperDapp</span>.</li>
            <li>Unify tokens into <span className="font-semibold">$ABX</span> with broader <span className="font-semibold">yield & utility</span>.</li>
            <li>Reduce duplicated costs and ship features faster under one brand.</li>
            <li>Strengthen the Alephium ecosystem with a single, liquid token.</li>
          </ul>
        </div>

        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <h3 className="text-white font-semibold mb-2">Swap & Voting Details</h3>
          <div className="text-gray-300 text-sm space-y-2">
            <p>
              <span className="font-semibold">Fixed swap:</span> <span className="text-white">21.5 $APAD = 1 $ABX</span> (≈ 1:1 USD value).
            </p>
            <p>
              <span className="font-semibold">Eligibility:</span> $APAD stakers (by tier) can vote on the proposal.
            </p>
            <div>
              <p className="font-semibold mb-1">Tier weighting:</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <Badge>🥉 Bronze = 1 vote</Badge>
                <Badge>🥈 Silver = 2 votes</Badge>
                <Badge>🥇 Gold = 3 votes</Badge>
                <Badge>🏆 Platinum = 4 votes</Badge>
                <Badge className="col-span-2">💎 Diamond = 5 votes</Badge>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* User Status */}
      <div className="mb-6 bg-gray-900 rounded-xl border border-gray-700 p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="text-gray-300 text-sm">
          <p><span className="text-gray-400">Wallet:</span> <span className="text-white">{account ?? "Not connected"}</span></p>
          <p><span className="text-gray-400">Tier:</span> <span className="text-white">{tierLabel(userTier)}</span></p>
          <p><span className="text-gray-400">Eligible:</span> <span className={`font-semibold ${eligible ? "text-green-400" : "text-red-400"}`}>{eligible ? "Yes" : "No"}</span></p>
        </div>
        {!account && (
          <p className="text-red-400 text-sm font-semibold">Connect wallet to vote.</p>
        )}
        {account && !eligible && (
          <p className="text-yellow-300 text-sm">Stake APAD to become eligible for voting.</p>
        )}
      </div>

      {/* Vote Box */}
      <div className="mb-6 bg-gray-800 rounded-xl p-4 border border-gray-700">
        <h3 className="text-white font-semibold mb-3">Cast Your Vote</h3>

        {/* Current choice */}
        <div className="mb-3 text-sm">
          <span className="text-gray-400 mr-2">Your current vote:</span>
          <span className={`font-semibold ${currentUserVote === "yes" ? "text-green-400" : currentUserVote === "no" ? "text-red-400" : "text-gray-400"}`}>
            {currentUserVote ? currentUserVote.toUpperCase() : "NONE"}
          </span>
        </div>

        {/* Choice buttons */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setLocalChoice("yes")}
            className={`flex-1 px-4 py-2 rounded-md font-semibold border transition ${localChoice === "yes"
              ? "bg-green-600 border-green-500 text-white"
              : "bg-gray-900 border-gray-700 text-green-300 hover:border-green-500"
              }`}
          >
            ✅ Yes — Merge & Swap
          </button>
          <button
            type="button"
            onClick={() => setLocalChoice("no")}
            className={`flex-1 px-4 py-2 rounded-md font-semibold border transition ${localChoice === "no"
              ? "bg-red-600 border-red-500 text-white"
              : "bg-gray-900 border-gray-700 text-red-300 hover:border-red-500"
              }`}
          >
            ❌ No — Keep Separate
          </button>
        </div>

        {/* Submit */}
        <div className="mt-4">
          <button
            onClick={handleSubmit}
            disabled={!canVote || !localChoice || pending}
            className="w-full px-4 py-2 bg-red-600 text-white rounded-md font-semibold hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed"
          >
            {pending ? "Submitting..." : localChoice ? `Submit: ${localChoice.toUpperCase()}` : "Select an option to vote"}
          </button>
          {!votingOpen && (
            <p className="text-xs text-gray-400 mt-2">
              Voting Closed.
            </p>
          )}
        </div>
      </div>

      {/* Footer note */}
      <p className="text-xs text-gray-500">
        Votes are weighted per tier to reflect both the majority’s will and the commitment of large stakers. Changes to your vote are allowed until voting closes.
      </p>
    </div>
  );
}

/* Helpers */
function Badge({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={`inline-block px-2 py-1 rounded bg-gray-900 border border-gray-700 text-gray-200 ${className}`}>
      {children}
    </span>
  );
}
function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
